import axios from "axios";

class HttpService {
  static HTTP_METHODS = {
    GET: "get",
    POST: "post",
    PUT: "put",
    DELETE: "delete"
  };

  static headers = {
    "Content-Type": "application/json"
  };

  static INTERNET_ERROR = {
    OFFLINE: "ERR_NETWORK",
    CONNECTION_ABORTED: "ECONNABORTED",
    NETWORK_ERROR: "Network Error"
  }

  static retryConfig = {
    maxRetryCount: 5,
    retryDelay: 2000, // milliseconds
    maxRetryDelay: 64000, // milliseconds
    retryStatusCodes: [502, 503, 504] // Retry on these HTTP status codes
  };

  static setHeaders(headers) {
    return { ...HttpService.headers, ...headers };
  }

  // change: makeRequest now returns entire response object instead of just data
  static async makeRequest(method, baseURL, url, data, headers = {}, retryOnOffline = true) {
    let timer = HttpService.retryConfig.retryDelay;
    let callInProgress = true;

    while (callInProgress) {
      try {
        let config = {};
        if (baseURL) {
          config = {
            method,
            baseURL,
            url,
            headers: HttpService.setHeaders(headers),
            data,
            withCredentials: true
          };
        } else {
          config = {
            method,
            url,
            headers: HttpService.setHeaders(headers),
            data
          };
        }
        
        callInProgress = false;
        const response = await axios(config);
        return response;
      } catch (error) {
        if (
          retryOnOffline &&
          error.code === HttpService.INTERNET_ERROR.OFFLINE || 
          error.code === HttpService.INTERNET_ERROR.CONNECTION_ABORTED || 
          error.message === HttpService.INTERNET_ERROR.NETWORK_ERROR
        ) {
          // retry the failed API call
          callInProgress = true;
          await new Promise((resolve) => setTimeout(resolve, timer));
          // exponential backoff
          timer = timer * 2;
          if (timer > this.retryConfig.maxRetryDelay){
            timer = HttpService.retryConfig.retryDelay;
          }
        } else {
          throw error;
        }
      }
    }
  }

  static async getRequest(baseURL, url, headers = {}) {
    try {
      const response = await HttpService.makeRequest(HttpService.HTTP_METHODS.GET, baseURL, url, null, headers);
      return response;
    } catch (error) {
      throw error
    }
  }

  static async postRequest(baseURL, url, data, headers = {}, retryOnOffline = true) {
    try {
      const response = await HttpService.makeRequest(HttpService.HTTP_METHODS.POST, baseURL, url, data, headers, retryOnOffline);
      return response;
    } catch (error) {
      throw error
    }
  }

  static async getRequestWithRetry(baseURL, url, headers = {}) {
    let retryCounter = 0;
    let retryDelay = HttpService.retryConfig.retryDelay;
    let lastError;
    while (retryCounter < HttpService.retryConfig.maxRetryCount) {
      try {
        const response = await HttpService.getRequest(baseURL, url, headers);
        return response;
      } catch (error) {
        if (HttpService.retryConfig.retryStatusCodes.includes(error.response?.status)) {
          retryCounter++;
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          retryDelay *= 2; // Exponential backoff
          lastError = error; // Store the last error for throwing later
        } else {
          throw error;
        }
      }
    }

    throw lastError;
  }

  static async postRequestWithRetry(baseURL, url, data, headers = {}, retryOnOffline = true) {
    let retryCounter = 0;
    let retryDelay = HttpService.retryConfig.retryDelay;
    let lastError;

    while (retryCounter < HttpService.retryConfig.maxRetryCount) {
      try {
        const response = await HttpService.postRequest(baseURL, url, data, headers, retryOnOffline);
        return response;
      } catch (error) {
        if (HttpService.retryConfig.retryStatusCodes.includes(error.response?.status)) {
          retryCounter++;
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          retryDelay *= 2; // Exponential backoff
          lastError = error; // Store the last error for throwing later
        } else {
          throw error;
        }
      }
    }
    throw lastError;
  }

  static putRequest(baseURL, url, data, headers = {}) {
    return HttpService.makeRequest(HttpService.HTTP_METHODS.PUT, baseURL, url, data, headers);
  }

  static deleteRequest(baseURL, url, data, headers = {}) {
    return HttpService.makeRequest(HttpService.HTTP_METHODS.DELETE, baseURL, url, data, headers);
  }
}

// Export the Http service
export default HttpService;
